import { useState } from "react";

export interface SeriesPoint {
  label: string;
  value: number;
}

interface LineChartProps {
  data: SeriesPoint[];
  height?: number;
}

const W = 700;
const H = 280;
const PAD_L = 40;
const PAD_R = 16;
const PAD_T = 18;
const PAD_B = 30;

function niceMax(value: number): number {
  const raw = Math.max(4, value);
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / magnitude;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * magnitude;
}

export default function LineChart({ data, height = H }: LineChartProps) {
  const [hover, setHover] = useState<number | null>(null);

  const n = data.length;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const max = niceMax(Math.max(...data.map((d) => d.value)));
  const yMax = max;
  const yTicks = 5;

  const xFor = (i: number) => PAD_L + (i / Math.max(1, n - 1)) * innerW;
  const yFor = (v: number) => PAD_T + innerH - (v / yMax) * innerH;

  const points = data.map((d, i) => ({ x: xFor(i), y: yFor(d.value), value: d.value, label: d.label }));
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${xFor(n - 1).toFixed(1)},${PAD_T + innerH} L${PAD_L},${PAD_T + innerH} Z`;

  const gridlines = Array.from({ length: yTicks }, (_, i) => {
    const ratio = i / (yTicks - 1);
    const y = PAD_T + innerH - ratio * innerH;
    const val = (ratio * yMax).toFixed(0);
    return { y, val };
  });

  const labelStep = Math.max(1, Math.ceil((n - 1) / 5));
  const xLabels = points.filter((_, i) => i % labelStep === 0 || i === n - 1);

  const hoverP = hover != null ? points[hover] : null;
  const hoverLeft = hover != null ? (hover / (n - 1)) * 100 : 0;
  const hoverTop = hoverP ? (1 - hoverP.value / yMax) * 100 : 0;

  return (
    <div
      className="relative w-full"
      style={{ height }}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        setHover(Math.round(ratio * (n - 1)));
      }}
      onMouseLeave={() => setHover(null)}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="h-full w-full"
        role="img"
        aria-label="Mandate activity chart"
      >
        <defs>
          <linearGradient id="mandateLineFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1E40AF" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#1E40AF" stopOpacity="0" />
          </linearGradient>
        </defs>

        {gridlines.map((g) => (
          <g key={g.y}>
            <line x1={PAD_L} y1={g.y} x2={W - PAD_R} y2={g.y} stroke="#F1F5F9" strokeWidth="1" />
            <text
              x={PAD_L - 8}
              y={g.y + 4}
              textAnchor="end"
              className="fill-slate-400"
              fontSize="11"
            >
              {g.val}
            </text>
          </g>
        ))}

        <path d={areaPath} fill="url(#mandateLineFill)" />
        <path d={linePath} fill="none" stroke="#1E40AF" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

        {hoverP && (
          <circle
            cx={hoverP.x}
            cy={hoverP.y}
            r="5"
            fill="#1E40AF"
            stroke="#FFFFFF"
            strokeWidth="2"
          />
        )}

        {xLabels.map((p) => (
          <text
            key={p.label + p.x}
            x={p.x}
            y={H - 10}
            textAnchor="middle"
            className="fill-slate-400"
            fontSize="11"
          >
            {p.label}
          </text>
        ))}
      </svg>

      {hoverP && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-line bg-ink px-3 py-2 shadow-dropdown"
          style={{ left: `${hoverLeft}%`, top: `${hoverTop}%` }}
        >
          <p className="text-[11px] text-slate-300">{hoverP.label}</p>
          <p className="text-[13px] font-semibold text-white">{hoverP.value} collected</p>
        </div>
      )}
    </div>
  );
}